import { useParams, useNavigate, Link } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { ErrorMessage } from '@/components/common/ErrorMessage'
import { useDevice, useCreateDevice, useUpdateDevice, useDeleteDevice } from '@/hooks/useDevices'
import { useAccounts } from '@/hooks/useAccounts'
import { useToast } from '@/components/ui/use-toast'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { deviceService } from '@/services/device.service'
import type { DeviceParametersUpdateDto } from '@/types/device.types'
import { ArrowLeft, Droplets, Save, AlertTriangle, Trash2, MapPin } from 'lucide-react'
import { DeviceLocationPicker } from '@/components/map/DeviceLocationPicker'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'

const deviceSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  serialNumber: z.string().min(1, 'Serial number is required'),
  deviceType: z.enum(['watersense', 'rk900', 'dht11soil']),
  accountId: z.string().min(1, 'Account is required'),
  modemId: z.string().optional(),
  hardwareVersion: z.string().optional(),
  firmwareVersion: z.string().optional(),
  notes: z.string().optional(),
  lat: z.union([z.coerce.number(), z.null()]).optional(),
  long: z.union([z.coerce.number(), z.null()]).optional(),
})

type DeviceFormData = z.infer<typeof deviceSchema>

export const DeviceEditPage = () => {
  const { deviceId } = useParams<{ deviceId: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const isEditMode = deviceId && deviceId !== 'new'

  const { data: device, isLoading: deviceLoading } = useDevice(isEditMode ? deviceId! : '')
  const { data: accounts, isLoading: accountsLoading, error: accountsError } = useAccounts()
  const createDevice = useCreateDevice()
  const updateDevice = useUpdateDevice()
  const deleteDevice = useDeleteDevice()

  // Delete confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')

  // WaterSense parameters state
  const [valueFull, setValueFull] = useState<number | undefined>()
  const [valueEmpty, setValueEmpty] = useState<number | undefined>()
  const [valueUnplugged, setValueUnplugged] = useState<number | undefined>()
  const [calibrateFull, setCalibrateFull] = useState<number | undefined>()
  const [hasRainbucket, setHasRainbucket] = useState(false)
  const [vesselType, setVesselType] = useState<string | undefined>()
  const [levelType, setLevelType] = useState<string | undefined>()

  // Fetch WaterSense parameters if device is watersense type
  const isWaterSense = device?.deviceType?.toLowerCase().includes('watersense') ||
                       device?.deviceType?.toLowerCase().includes('water')

  const { data: watersenseParams, isLoading: isLoadingParams, error: paramsError } = useQuery({
    queryKey: ['device-parameters', deviceId],
    queryFn: () => deviceService.getDeviceParameters(deviceId!),
    enabled: isEditMode && isWaterSense,
    retry: false,
    // Don't throw on 404 (not found) or network errors - parameters might not exist yet
    throwOnError: false
  })

  // Load parameters when data arrives
  useEffect(() => {
    if (watersenseParams) {
      setValueFull(watersenseParams.valueFull)
      setValueEmpty(watersenseParams.valueEmpty)
      setValueUnplugged(watersenseParams.valueUnplugged)
      setCalibrateFull(watersenseParams.calibrateFull)
      setHasRainbucket(watersenseParams.hasRainbucket || false)
      setVesselType(watersenseParams.vesselType)
      setLevelType(watersenseParams.levelType)
    }
  }, [watersenseParams, paramsError])

  // Mutation for updating parameters
  const updateParamsMutation = useMutation({
    mutationFn: (params: DeviceParametersUpdateDto) =>
      deviceService.updateDeviceParameters(deviceId!, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['device-parameters', deviceId] })
      toast({
        title: 'Success',
        description: 'Device parameters updated successfully',
      })
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to update device parameters',
        variant: 'destructive',
      })
    }
  })

  const handleSaveWaterSenseParams = async () => {
    const params: DeviceParametersUpdateDto = {
      valueFull,
      valueEmpty,
      valueUnplugged,
      calibrateFull,
      hasRainbucket,
      vesselType,
      levelType
    }
    await updateParamsMutation.mutateAsync(params)
  }

  const handleDelete = async () => {
    if (!device || deleteConfirmText !== device.name) {
      toast({
        title: 'Error',
        description: 'Device name does not match',
        variant: 'destructive'
      })
      return
    }

    try {
      await deleteDevice.mutateAsync(deviceId!)
      toast({
        title: 'Success',
        description: 'Device deleted successfully'
      })
      navigate('/devices')
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete device',
        variant: 'destructive'
      })
    } finally {
      setDeleteConfirmOpen(false)
      setDeleteConfirmText('')
    }
  }

  const form = useForm<DeviceFormData>({
    resolver: zodResolver(deviceSchema),
    defaultValues: {
      name: '',
      serialNumber: '',
      deviceType: 'watersense',
      accountId: '',
      modemId: '',
      hardwareVersion: '',
      firmwareVersion: '',
      notes: '',
      lat: null,
      long: null,
    }
  })

  // Update form when device data loads
  useEffect(() => {
    if (device && !form.formState.isDirty) {
      form.reset({
        name: device.name || '',
        serialNumber: device.serialNumber || '',
        deviceType: device.deviceType || 'watersense',
        accountId: device.accountId || '',
        modemId: device.modemId || '',
        hardwareVersion: device.hardwareVersion || '',
        firmwareVersion: device.firmwareVersion || '',
        notes: device.notes || '',
        lat: device.lat ?? null,
        long: device.long ?? null,
      })
    }
  }, [device, form])

  const onSubmit = async (data: DeviceFormData) => {
    try {
      if (isEditMode) {
        await updateDevice.mutateAsync({ deviceId: deviceId!, device: data as any })
        toast({
          title: 'Success',
          description: 'Device updated successfully'
        })
      } else {
        await createDevice.mutateAsync(data)
        toast({
          title: 'Success',
          description: 'Device created successfully'
        })
      }
      navigate('/devices')
    } catch (error) {
      toast({
        title: 'Error',
        description: `Failed to ${isEditMode ? 'update' : 'create'} device`,
        variant: 'destructive'
      })
    }
  }

  if (deviceLoading || accountsLoading) {
    return <LoadingSpinner />
  }

  if (isEditMode && !device) {
    return <ErrorMessage message="Device not found." />
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/devices">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <h1 className="text-3xl font-bold">
          {isEditMode ? 'Edit Device' : 'New Device'}
        </h1>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Device Name *</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., Water Tank 1" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="serialNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Serial Number *</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., WS-12345" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="deviceType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Device Type *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="watersense">Water Sense</SelectItem>
                          <SelectItem value="rk900">RK900</SelectItem>
                          <SelectItem value="dht11soil">DHT11 Soil</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="accountId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Account *</FormLabel>
                      {accountsError ? (
                        <p className="text-sm text-destructive">
                          Failed to load accounts.
                        </p>
                      ) : (
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select an account" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {accounts?.accounts?.map((account) => (
                              <SelectItem key={account.id} value={account.id}>
                                {account.name}
                              </SelectItem>
                            )) || []}
                          </SelectContent>
                        </Select>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Technical Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="modemId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Modem ID</FormLabel>
                    <FormControl>
                      <Input placeholder="Optional" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="hardwareVersion"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Hardware Version</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., v1.2" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="firmwareVersion"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Firmware Version</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., 2.1.0" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notes</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Add any additional notes about this device"
                        rows={4}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Location
              </CardTitle>
              <CardDescription>
                Drag the marker on the map or type coordinates to set the device location.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="lat"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Latitude</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.000001"
                          placeholder="e.g., -41.2865"
                          value={field.value ?? ''}
                          onChange={(e) => field.onChange(e.target.value === '' ? null : Number(e.target.value))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="long"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Longitude</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.000001"
                          placeholder="e.g., 174.7762"
                          value={field.value ?? ''}
                          onChange={(e) => field.onChange(e.target.value === '' ? null : Number(e.target.value))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <DeviceLocationPicker
                lat={form.watch('lat') ?? null}
                long={form.watch('long') ?? null}
                onChange={(lat, long) => {
                  form.setValue('lat', lat, { shouldDirty: true })
                  form.setValue('long', long, { shouldDirty: true })
                }}
              />
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" asChild>
              <Link to="/devices">Cancel</Link>
            </Button>
            <Button
              type="submit"
              disabled={createDevice.isPending || updateDevice.isPending}
            >
              {isEditMode ? 'Update Device' : 'Create Device'}
            </Button>
          </div>
        </form>
      </Form>

      {/* WaterSense Calibration Parameters - Only for existing WaterSense devices */}
      {isEditMode && isWaterSense && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Droplets className="h-5 w-5" />
              WaterSense Calibration Parameters
            </CardTitle>
            <CardDescription>
              Configure vessel dimensions and sensor calibration values
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {isLoadingParams ? (
              <div className="text-sm text-muted-foreground">Loading parameters...</div>
            ) : (
              <>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="value-full">Full Level (mm)</Label>
                    <Input
                      id="value-full"
                      type="number"
                      step="0.1"
                      value={valueFull ?? ''}
                      onChange={(e) => setValueFull(e.target.value ? Number(e.target.value) : undefined)}
                      placeholder="e.g., 2000"
                    />
                    <p className="text-xs text-muted-foreground">
                      Sensor reading when vessel is full
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="value-empty">Empty Level (mm)</Label>
                    <Input
                      id="value-empty"
                      type="number"
                      step="0.1"
                      value={valueEmpty ?? ''}
                      onChange={(e) => setValueEmpty(e.target.value ? Number(e.target.value) : undefined)}
                      placeholder="e.g., 0"
                    />
                    <p className="text-xs text-muted-foreground">
                      Sensor reading when vessel is empty
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="value-unplugged">Unplugged Value (mm)</Label>
                    <Input
                      id="value-unplugged"
                      type="number"
                      step="0.1"
                      value={valueUnplugged ?? ''}
                      onChange={(e) => setValueUnplugged(e.target.value ? Number(e.target.value) : undefined)}
                      placeholder="e.g., 9999"
                    />
                    <p className="text-xs text-muted-foreground">
                      Reading indicating sensor is unplugged
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="calibrate-full">Calibration Full Value</Label>
                    <Input
                      id="calibrate-full"
                      type="number"
                      step="0.1"
                      value={calibrateFull ?? ''}
                      onChange={(e) => setCalibrateFull(e.target.value ? Number(e.target.value) : undefined)}
                      placeholder="e.g., 2000"
                    />
                    <p className="text-xs text-muted-foreground">
                      Calibration value for full level
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="vessel-type">Vessel Type</Label>
                    <Select value={vesselType || ''} onValueChange={setVesselType}>
                      <SelectTrigger id="vessel-type">
                        <SelectValue placeholder="Select vessel type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Tank">Tank</SelectItem>
                        <SelectItem value="Pond">Pond</SelectItem>
                        <SelectItem value="Dam">Dam</SelectItem>
                        <SelectItem value="Reservoir">Reservoir</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Type of water vessel being monitored
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="level-type">Level Type</Label>
                    <Input
                      id="level-type"
                      type="text"
                      value={levelType ?? ''}
                      onChange={(e) => setLevelType(e.target.value || undefined)}
                      placeholder="e.g., Ultrasonic"
                    />
                    <p className="text-xs text-muted-foreground">
                      Type of level sensor
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between space-x-2 pt-4 border-t">
                  <div className="space-y-0.5">
                    <Label htmlFor="has-rainbucket">Rain Bucket Installed</Label>
                    <p className="text-xs text-muted-foreground">
                      Device has rain bucket sensor attached
                    </p>
                  </div>
                  <Switch
                    id="has-rainbucket"
                    checked={hasRainbucket}
                    onCheckedChange={setHasRainbucket}
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    onClick={handleSaveWaterSenseParams}
                    disabled={updateParamsMutation.isPending}
                  >
                    <Save className="mr-2 h-4 w-4" />
                    {updateParamsMutation.isPending ? 'Saving...' : 'Save Parameters'}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Danger Zone - Only show in edit mode */}
      {isEditMode && (
        <Card className="border-red-200 bg-red-50/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" />
              Danger Zone
            </CardTitle>
            <CardDescription>
              Irreversible and destructive actions
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 border border-red-200 rounded-lg bg-white">
              <div className="space-y-1">
                <p className="font-medium text-red-900">Delete this device</p>
                <p className="text-sm text-red-700">
                  Once deleted, all device data and measurements will be permanently removed. This action cannot be undone.
                </p>
              </div>
              <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" className="flex-shrink-0">
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete Device
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete the device
                      <strong className="block mt-2 text-foreground">{device?.name}</strong>
                      and remove all associated data from our servers.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <div className="space-y-2 py-4">
                    <Label htmlFor="delete-confirm">
                      Type <strong>{device?.name}</strong> to confirm deletion:
                    </Label>
                    <Input
                      id="delete-confirm"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder="Enter device name"
                      className="font-mono"
                    />
                  </div>
                  <AlertDialogFooter>
                    <AlertDialogCancel onClick={() => setDeleteConfirmText('')}>
                      Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDelete}
                      disabled={deleteConfirmText !== device?.name || deleteDevice.isPending}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      {deleteDevice.isPending ? 'Deleting...' : 'Delete Device'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
